import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card, StatusBadge, Avatar, PageHeader,
} from "components/dashboard/ui";
import {
  Trophy, GitBranch, AlertTriangle, ShieldCheck, Boxes,
  Receipt, Users2, Wallet,
} from "lucide-react";
import {
  BarChart, Bar, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
  CartesianGrid, Legend, LineChart, Line,
} from "recharts";
import {
  useOwnerProjectSummary,
  useWeather,
} from "../../../utils/hooks/useSiteData";
import ProjectSummaryHeader from "./ProjectSummaryHeader";
import NewMaterialRequest from "../Materials/MaterialRequest/NewRequest";

const WEATHER_API_KEY = "c093db40219c1637dbc236b56549afdd";

const fallbackBoqData = [
  { cat: "Foundation", plan: 100, actual: 100 },
  { cat: "Structure", plan: 78, actual: 72 },
  { cat: "MEP", plan: 55, actual: 49 },
  { cat: "Finishing", plan: 22, actual: 18 },
  { cat: "External", plan: 8, actual: 4 },
];

const fallbackAttendanceData = [
  { d: "Mon", v: 88 }, { d: "Tue", v: 86 }, { d: "Wed", v: 91 },
  { d: "Thu", v: 93 }, { d: "Fri", v: 92 }, { d: "Sat", v: 94 }, { d: "Sun", v: 86 },
];

const timelineData = [
  { week: "W15", plan: 44, executed: 42 },
  { week: "W16", plan: 48, executed: 47 },
  { week: "W17", plan: 55, executed: 53 },
  { week: "W18", plan: 62, executed: 59 },
  { week: "W19", plan: 69, executed: 66 },
  { week: "W20", plan: 76, executed: 72 },
];

const fallbackCostData = [
  { phase: "Site prep", actual: 1.62, estimate: 1.8, delta: "-₹18,000", tone: "good" },
  { phase: "Foundation", actual: 4.98, estimate: 5.2, delta: "-₹22,000", tone: "good" },
  { phase: "Structural", actual: 8.2, estimate: 7.8, delta: "+₹40,000", tone: "alert" },
  { phase: "Brickwork", actual: 3.8, estimate: 3.4, delta: "+₹40,000", tone: "alert" },
  { phase: "Plumbing", actual: 2.6, estimate: 2.8, delta: "-₹20,000", tone: "good" },
  { phase: "Electrical", actual: 2.9, estimate: 3.2, delta: "-₹30,000", tone: "good" },
  { phase: "Finishing", actual: 0, estimate: 4.9, delta: "not started", tone: "neutral" },
];

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, safeNumber(value)));

const formatCurrencyCompact = (value) => {
  const number = safeNumber(value);
  const abs = Math.abs(number);
  const sign = number < 0 ? "-" : "";

  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(1)}L`;
  return `${sign}₹${abs.toLocaleString("en-IN")}`;
};

const formatSignedCurrency = (value, type) => {
  const prefix = type === "in" ? "+" : "-";
  return `${prefix}${formatCurrencyCompact(Math.abs(safeNumber(value)))}`;
};

const formatDate = (value, options = { day: "2-digit", month: "short" }) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", options);
};

const getInitials = (firstName, lastName) => {
  const name = `${firstName || ""} ${lastName || ""}`.trim();
  if (!name) return "TM";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
};

const getTransactionType = (category) =>
  ["received", "refund"].includes(String(category || "").toLowerCase()) ? "in" : "out";

const toTitleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const parseStoredOrganizationId = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}")?.id || null;
  } catch (_) {
    return null;
  }
};

const AttendanceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload || {};

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold text-foreground">{label}</div>
      <div className="space-y-1 text-muted-foreground">
        <div className="flex min-w-36 justify-between gap-4">
          <span>Present</span>
          <span className="font-semibold text-foreground">{safeNumber(data.present)}</span>
        </div>
        <div className="flex min-w-36 justify-between gap-4">
          <span>Absent</span>
          <span className="font-semibold text-foreground">{safeNumber(data.absent)}</span>
        </div>
        <div className="flex min-w-36 justify-between gap-4">
          <span>Attendance rate</span>
          <span className="font-semibold text-foreground">{safeNumber(data.attendanceRate)}%</span>
        </div>
      </div>
    </div>
  );
};

function ProjectOwner() {
  const [planningTab, setPlanningTab] = useState("boq");
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const { siteId: routeSiteId } = useParams();
  const siteId = routeSiteId || localStorage.getItem("site");
  const organizationId = useMemo(() => parseStoredOrganizationId(), []);
  const isOwnerRole = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return String(user?.profession || "").toLowerCase() === "owner";
    } catch (_) {
      return false;
    }
  }, []);

  const { data: summary } = useOwnerProjectSummary(organizationId, siteId);
  const siteOverview = summary?.site_overview || {};
  const tasksOverview = summary?.tasks_overview || {};
  const planningOverview = summary?.planning_overview || {};
  const attendanceTrend = summary?.attendance_trend || {};
  const inventoryAlerts = summary?.inventory_alerts || {};
  const financeOverview = summary?.finance_overview || {};
  const teamOverview = summary?.team_overview || {};
  const recentTransactions = summary?.recent_transactions || {};
  const city = siteOverview.city || siteOverview.state;
  const {
    data: weather,
    isLoading: weatherLoading,
    isError: weatherError,
  } = useWeather(city, WEATHER_API_KEY);

  const boqData = useMemo(() => {
    const sections = Array.isArray(planningOverview.sections) ? planningOverview.sections : [];
    if (!sections.length) return [];

    return sections.map((section, index) => {
      const estimated = safeNumber(section.total_estimated);
      const actual = safeNumber(section.total_actual_cost);
      return {
        cat: section.section_name || `Section ${index + 1}`,
        plan: estimated ? 100 : 0,
        actual: estimated ? clamp((actual / estimated) * 100).toFixed(2) : 0,
      };
    });
  }, [planningOverview.sections]);

  const costData = useMemo(() => {
    const sections = Array.isArray(planningOverview.sections) ? planningOverview.sections : [];
    if (!sections.length) return [];

    return sections.map((section, index) => {
      const estimate = safeNumber(section.total_estimated) / 100000;
      const actual = safeNumber(section.total_actual_cost) / 100000;
      const delta = safeNumber(section.total_actual_cost) - safeNumber(section.total_estimated);
      return {
        phase: section.section_name || `Section ${index + 1}`,
        actual,
        estimate,
        delta: delta === 0 ? "on plan" : formatSignedCurrency(delta, delta > 0 ? "in" : "out"),
        tone: delta > 0 ? "alert" : delta < 0 ? "good" : "neutral",
      };
    });
  }, [planningOverview.sections]);

  const maxCost = Math.max(1, ...costData.map((item) => Math.max(item.actual, item.estimate)));
  const taskBadges = [
    { label: `${safeNumber(tasksOverview.ongoing_tasks)} Ongoing`, className: "bg-amber-100 text-amber-700" },
    { label: `${safeNumber(tasksOverview.delayed_tasks)} Delayed`, className: "bg-red-100 text-red-700" },
    { label: `${safeNumber(tasksOverview.completed_tasks)} Completed`, className: "bg-blue-100 text-blue-700" },
  ];
  const delayedTasks = Array.isArray(tasksOverview.top_delayed_tasks)
    ? tasksOverview.top_delayed_tasks.slice(0, 3)
    : [];
  const teamMembers = Array.isArray(teamOverview.members)
    ? teamOverview.members.slice(0, 5)
    : [];

  const attendanceData = useMemo(() => {
    const lastSevenDays = Array.isArray(attendanceTrend.last_seven_days)
      ? attendanceTrend.last_seven_days
      : [];

    if (!lastSevenDays.length) return [];

    return lastSevenDays.map((dayEntry) => {
      const [dateKey, values = {}] = Object.entries(dayEntry || {})[0] || [];
      const present = safeNumber(values.present);
      const total = safeNumber(values.total);
      const absent = Math.max(
        0,
        safeNumber(values.absent ?? values.absents, total - present),
      );
      const attendanceRate = total ? Math.round((present / total) * 100) : 0;
      return {
        d: dateKey ? formatDate(dateKey, { weekday: "short" }) : "-",
        present,
        absent,
        attendanceRate,
        v: attendanceRate,
      };
    });
  }, [attendanceTrend.last_seven_days]);

  const attendanceAverage = attendanceData.length
    ? Math.round(attendanceData.reduce((sum, item) => sum + safeNumber(item.v), 0) / attendanceData.length)
    : 0;
  const absenteeFlags = Array.isArray(attendanceTrend.most_absentees)
    ? attendanceTrend.most_absentees.slice(0, 2)
    : [];
  const totalAbsents = absenteeFlags.reduce((sum, worker) => sum + safeNumber(worker.absents), 0);

  const lowStocks = Array.isArray(inventoryAlerts.low_stocks) ? inventoryAlerts.low_stocks : [];
  const inventoryRows = lowStocks.length
    ? lowStocks.slice(0, 5).map((item) => {
      const currentStock = safeNumber(item.current_stock);
      const lastStock = item.last_stock == null ? null : safeNumber(item.last_stock);
      const isCritical = lastStock != null ? currentStock <= lastStock : currentStock <= 0;
      return {
        m: item.material_name || `Material #${item.material_id}`,
        eta: `${currentStock} ${item.uom} in stock`,
        lastPO: item.last_ordered ? `Last Purchase: ${item.last_purchase_order} on ${formatDate(item.last_ordered)}` : "",
        tone: isCritical ? "alert" : "warn",
        dot: isCritical ? "bg-red-500" : "bg-amber-500",
      };
    })
    : [{ m: "No low stock alerts", eta: "Inventory stable", tone: "good", dot: "bg-emerald-500" }];

  const estimatedBudget = safeNumber(financeOverview.estimated_budget);
  const totalExpenses = safeNumber(financeOverview.total_expenses);
  const budgetUsed = estimatedBudget ? Math.round((totalExpenses / estimatedBudget) * 100) : 0;
  const cashFlowData = [
    {
      label: "Received",
      value: formatCurrencyCompact(financeOverview.received_expenses),
      sub: `${formatCurrencyCompact(financeOverview.refund_expenses)} refunded`,
      tone: "text-emerald-600",
    },
    {
      label: "Spent",
      value: formatCurrencyCompact(totalExpenses),
      sub: estimatedBudget ? `${budgetUsed}% of budget` : "Budget not set",
      tone: "text-foreground",
    },
    {
      label: "Dues from worker",
      value: formatCurrencyCompact(financeOverview.worker_dues),
      sub: `${formatCurrencyCompact(financeOverview.worker_expenses)} worker expenses`,
      tone: "text-amber-600",
    },
    {
      label: "Dues to vendors",
      value: formatCurrencyCompact(financeOverview.vendor_dues),
      sub: `${formatCurrencyCompact(financeOverview.material_expenses)} material expenses`,
      tone: "text-foreground",
    },
  ];

  const transactionRows = Array.isArray(recentTransactions.last_five)
    ? recentTransactions.last_five.filter((trn)=>trn.transaction_date === new Date().toISOString().split("T")[0]).map((transaction) => {
      const type = getTransactionType(transaction.category);
      return {
        d: formatDate(transaction.transaction_date),
        w: transaction.description || `Transaction #${transaction.transaction_id}`,
        c: toTitleCase(transaction.category) || "Finance",
        v: transaction.party || "-",
        a: formatSignedCurrency(transaction.amount, type),
        amount: safeNumber(transaction.amount),
        type,
        s: transaction.payment_method || "recorded",
        tone: "good",
      };
    })
    : [];
  const transactionInTotal = transactionRows
    .filter((row) => row.type === "in")
    .reduce((sum, row) => sum + row.amount, 0);
  const transactionOutTotal = transactionRows
    .filter((row) => row.type === "out")
    .reduce((sum, row) => sum + row.amount, 0);
  const transactionNet = transactionInTotal - transactionOutTotal;

  return (
    <div className="min-h-screen px-8 py-4">
      {/* <PageHeader title="Project Dashboard" eyebrow={siteOverview.site_name || "Project"}>
        <button className="px-3.5 py-2 rounded-xl text-sm font-medium border border-border bg-card hover:bg-muted">Compare Projects</button>
        <button className="px-3.5 py-2 rounded-xl text-sm font-semibold gradient-primary text-sm-primary-foreground shadow-sm hover:shadow-elevated transition-shadow">Executive Report</button>
      </PageHeader> */}

      <div className="grid grid-cols-12 gap-6">
        <ProjectSummaryHeader
          summary={summary}
          weather={weather}
          weatherLoading={weatherLoading}
          weatherError={weatherError}
        />

        {/* PLANNING VS EXECUTION */}
        <Card className="col-span-12 xl:col-span-8" title="Planning vs Execution" icon={<GitBranch className="size-3.5" />}
          action={
            <div className="flex gap-1 p-1 rounded-lg bg-muted text-[11px] font-bold uppercase">
              {[
                { key: "boq", label: "BoQ Progress" },
                // { key: "timeline", label: "Timeline" },
                { key: "cost", label: "Cost" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setPlanningTab(tab.key)}
                  className={`px-2.5 py-1 rounded transition-colors ${planningTab === tab.key ? "bg-sm-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          }>
          {planningTab === "boq" && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">% complete by category</span>
                <StatusBadge tone={safeNumber(tasksOverview.delayed_tasks) ? "warn" : "good"}>
                  {safeNumber(tasksOverview.delayed_tasks) ? `${safeNumber(tasksOverview.delayed_tasks)} delayed` : "on schedule"}
                </StatusBadge>
              </div>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={boqData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="#c3c3c3" vertical={false} />
                    <XAxis dataKey="cat" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgb(107, 114, 128)" }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "rgb(107, 114, 128)" }} unit="%" />
                    <Tooltip cursor={{ fill: "#c3c3c330" }} contentStyle={{ background: "#ffffff", border: "1px solid #c3c3c3", borderRadius: 8, fontSize: 12 }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="plan" name="Plan" fill="rgba(59,130,246,0.4)" stroke="rgb(59,130,246)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actual" name="Actual" fill="#3B0069" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {planningTab === "timeline" && (
            <section className="min-w-0">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Timeline</div>
                  <div className="mt-1 text-sm font-medium text-foreground">Plan vs executed tasks by week</div>
                </div>
                <div className="flex items-center gap-3 text-[11px] font-semibold text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="h-px w-5 border-t border-dashed border-muted-foreground" />Plan</span>
                  <span className="inline-flex items-center gap-1"><span className="h-0.5 w-5 bg-foreground" />Executed</span>
                </div>
              </div>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timelineData} margin={{ top: 12, right: 18, bottom: 8, left: -24 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="week" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "rgb(107, 114, 128)", fontWeight: 600 }} />
                    <YAxis hide domain={[35, 80]} />
                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="plan" name="Plan (BoQ)" stroke="rgb(156, 163, 175)" strokeWidth={2} strokeDasharray="3 4" dot={false} />
                    <Line type="monotone" dataKey="executed" name="Executed (tasks)" stroke="rgb(17, 24, 39)" strokeWidth={2.5} dot={{ r: 4, fill: "rgb(17, 24, 39)", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          {planningTab === "cost" && (
            <section className="min-w-0">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Cost</div>
                  <div className="mt-1 text-sm font-medium text-foreground">Phase-wise cost comparison</div>
                </div>
                <div className="flex items-center gap-3 text-[11px] font-semibold text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-blue-500" />Actual</span>
                  <span className="inline-flex items-center gap-1"><span className="h-4 w-px bg-amber-500" />Estimate</span>
                </div>
              </div>
              <div className="space-y-3">
                {costData.map((item) => {
                  const actualWidth = item.actual === 0 ? 0 : (item.actual / maxCost) * 100;
                  const estimateLeft = (item.estimate / maxCost) * 100;
                  const barClass = item.tone === "alert" ? "bg-red-500" : "bg-blue-500";
                  const deltaClass = item.tone === "alert" ? "text-red-600" : item.tone === "good" ? "text-emerald-600" : "text-muted-foreground";
                  return (
                    <div key={item.phase} className="grid grid-cols-[92px_1fr_116px] items-center gap-3 text-xs">
                      <div className="font-medium text-muted-foreground">{item.phase}</div>
                      <div className="relative h-3 rounded-full bg-muted">
                        <div className={`absolute inset-y-0 left-0 rounded-full ${barClass}`} style={{ width: `${actualWidth}%` }} />
                        <span className="absolute -top-1 bottom-[-4px] w-0.5 rounded-full bg-amber-500" style={{ left: `${estimateLeft}%` }} />
                      </div>
                      <div className="flex items-center justify-between gap-2 font-mono">
                        <span className="font-semibold text-foreground">{item.actual ? `₹${item.actual.toFixed(2)}L` : "-"}</span>
                        <span className={`font-bold ${deltaClass}`}>{item.delta}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </Card>

        {/* TASK GOVERNANCE */}
        <Card className="col-span-12 xl:col-span-4" title="Task Governance" icon={<ShieldCheck className="size-3.5" />}>
          <div className="flex gap-2 mb-4 flex-wrap">
            {taskBadges.map((badge) => (
              <span key={badge.label} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${badge.className}`}>{badge.label}</span>
            ))}
          </div>
          <div className="space-y-2">
            {(delayedTasks.length ? delayedTasks : [{ task_name: "No delayed tasks", task_group: "Schedule clear", days_delayed: 0 }]).map((task) => {
              const isDelayed = safeNumber(task.days_delayed) > 0;
              return (
                <div key={task.id || task.task_name} className={`flex items-center gap-3 p-3 rounded-lg border border-border border-l-4 ${isDelayed ? "border-l-red-500 bg-red-50/40" : "border-l-emerald-500 bg-emerald-50/40"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{task.task_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {task.task_group || task.status || "Task"} · {isDelayed ? `${task.days_delayed}d delayed` : "on track"}
                    </div>
                  </div>
                  {isDelayed && <button className="px-3 py-1 rounded-lg text-sm font-medium border border-red-300 text-red-600 hover:bg-red-50">Act →</button>}
                </div>
              );
            })}
          </div>
        </Card>

        {/* TEAM PERFORMANCE */}
        <Card className="col-span-12 md:col-span-6 xl:col-span-4" title="Team Performance" icon={<Trophy className="size-3.5" />}>
          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-medium">Team Members</h3>
              <div className="text-sm text-muted-foreground">Tasks/Subtasks</div>

            </div>

            {(teamMembers.length ? teamMembers : [{ first_name: "No", last_name: "members", profession: "Team unavailable", tasks_assigned: 0, subtasks_assigned: 0 }]).map((member, index) => {
              const tasksAssigned = safeNumber(member.tasks_assigned);
              const subtasksAssigned = safeNumber(member.subtasks_assigned);
              const tone = tasksAssigned + subtasksAssigned > 0 ? "info" : "neutral";
              const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || "Team member";
              return (
                <div key={member.member_id || member.user_id || name} className={`flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-lg ${index === 0 ? "border-l-4 border-l-amber-400" : ""}`}>
                  <div className="size-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold">{index + 1}</div>
                  <Avatar initials={getInitials(member.first_name, member.last_name)} tone={tone} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{name}</div>
                    <div className="text-[11px] text-muted-foreground">{member.profession || "Team"}</div>
                  </div>
                  <StatusBadge tone={tone}>{tasksAssigned}/{subtasksAssigned}</StatusBadge>
                </div>
              );
            })}
          </div>
        </Card>

        {/* ATTENDANCE TREND */}
        <Card className="col-span-12 md:col-span-6 xl:col-span-4" title="Worker Attendance Trend" icon={<Users2 className="size-3.5" />}>
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-3xl font-bold tracking-tight">{attendanceAverage}%</div>
              <div className="text-xs text-muted-foreground">avg this week</div>
            </div>
            <StatusBadge tone={totalAbsents ? "warn" : "good"}>{totalAbsents ? `${totalAbsents} absents` : "clear"}</StatusBadge>
          </div>
          <div className="h-[110px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={attendanceData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#c3c3c3" vertical={false} />
                <XAxis dataKey="d" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "rgb(107, 114, 128)" }} />
                <YAxis hide domain={[60, 100]} />
                <Tooltip cursor={{ fill: "#c3c3c330" }} content={<AttendanceTooltip />} />
                <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                  {attendanceData.map((d, i) => (
                    <Cell key={i} fill={d.v >= 90 ? "#10b981" : "#f59e0b"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Absenteeism flags</div>
            {(absenteeFlags.length ? absenteeFlags : [{ worker_name: "No absenteeism flags", worker_workname: "Attendance stable", absents: 0 }]).map((worker, index) => {
              const hasAbsents = safeNumber(worker.absents) > 0;
              return (
                <div key={`${worker.worker_name}-${index}`} className={`flex items-center gap-2 p-2 rounded-lg border ${hasAbsents ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                  <AlertTriangle className={`size-4 shrink-0 ${hasAbsents ? "text-red-600" : "text-emerald-600"}`} />
                  <span className="text-xs">{worker.worker_name}{worker.worker_workname ? ` · ${worker.worker_workname}` : ""}{hasAbsents ? ` · ${worker.absents} absents` : ""}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* INVENTORY ALERTS */}
        <Card className="col-span-12 xl:col-span-4" title="Inventory Alerts" icon={<Boxes className="size-3.5" />}
          action={!isOwnerRole ? (
            <button
              type="button"
              onClick={() => setShowNewRequestForm(true)}
              className="inline-flex h-7 items-center rounded-lg bg-[#3B0069] px-3 text-xs font-semibold text-white transition-colors hover:bg-[#2d0050]"
            >
              Raise MR
            </button>
          ) : null}>
          <div className="space-y-2">
            {inventoryRows.map((it) => (
              <div key={it.m} className={`flex items-center gap-3 p-3 rounded-lg border border-border ${it.tone === "alert" ? "bg-red-50/50" : ""}`}>
                <span className={`size-2.5 rounded-full ${it.dot} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{it.m}</div>
                  <div className="text-xs text-muted-foreground">{it.eta}</div>
                  <div className="text-xs text-muted-foreground">{it.lastPO}</div>
                </div>
                {/* removed StatusBadge ("Critical") per UI cleanup */}
              </div>
            ))}
          </div>
        </Card>

        {showNewRequestForm && (
          <NewMaterialRequest
            onClose={() => setShowNewRequestForm(false)}
          />
        )}

        {/* CASH FLOW SNAPSHOT */}
        <Card className="col-span-12" title="Cash Flow Snapshot" icon={<Wallet className="size-3.5" />}
          action={<span className="text-xs font-semibold text-muted-foreground">FY26 to date</span>}>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {cashFlowData.map((item) => (
              <div key={item.label} className="min-w-0">
                <div className="text-xs font-bold uppercase text-muted-foreground">{item.label}</div>
                <div className={`mt-2 text-2xl font-bold tracking-tight ${item.tone}`}>{item.value}</div>
                <div className="text-sm font-semibold text-muted-foreground">{item.sub}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* FINANCIAL DRILLDOWN */}
        <Card className="col-span-12" title="Financial Transactions · Today Payments" icon={<Receipt className="size-3.5" />}
          action={<button className="text-xs text-sm-primary font-semibold cursor-pointer hover:underline">Open ledger →</button>}>
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium text-left border-b border-border">
                  <th className="py-2.5 px-5 font-medium">Date</th>
                  <th className="py-2.5 font-medium">Description</th>
                  <th className="py-2.5 font-medium">Category</th>
                  <th className="py-2.5 font-medium">Party</th>
                  <th className="py-2.5 font-medium text-right">Amount</th>
                  <th className="py-2.5 px-5 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactionRows.length ? transactionRows.map((r, i) => (
                  <tr key={`${r.w}-${i}`} className={`${i % 2 === 1 ? "bg-muted/40" : ""} hover:bg-muted/70 transition-colors border-b border-border/50`}>
                    <td className="py-3 px-5 text-xs text-muted-foreground font-mono">{r.d}</td>
                    <td className="py-3 font-medium">{r.w}</td>
                    <td className="py-3"><span className="px-2 py-0.5 rounded-full bg-muted text-[11px] font-medium text-muted-foreground">{r.c}</span></td>
                    <td className="py-3 text-xs text-muted-foreground">{r.v}</td>
                    <td className={`py-3 text-right font-mono font-bold ${r.type === "in" ? "text-emerald-600" : r.type === "pending" ? "text-amber-600" : "text-red-600"}`}>{r.a}</td>
                    <td className="py-3 px-5 text-right"><StatusBadge tone={r.tone}>{r.s}</StatusBadge></td>
                  </tr>
                )) : (
                  <tr className="border-b border-border/50">
                    <td className="py-4 px-5 text-sm text-muted-foreground" colSpan={6}>No recent transactions</td>
                  </tr>
                )}
                <tr className="bg-sm-primary/5 font-semibold">
                  <td className="py-3 px-5 text-xs uppercase tracking-wide text-muted-foreground" colSpan={4}>Total</td>
                  <td className="py-3 text-right font-mono">
                    <span className="text-red-600">-{formatCurrencyCompact(transactionOutTotal)}</span> · <span className="text-emerald-600">+{formatCurrencyCompact(transactionInTotal)}</span> · <span>Net <span className={transactionNet >= 0 ? "text-emerald-600" : "text-red-600"}>{transactionNet >= 0 ? "+" : "-"}{formatCurrencyCompact(Math.abs(transactionNet))}</span></span>
                  </td>
                  <td className="py-3 px-5"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ProjectOwner;
